export const meta = {
  "title": "The CMS ACCESS Model: What FHIR Developers Need to Know",
  "slug": "cms-access-model-fhir-requirements",
  "excerpt": "CMS's ACCESS model mandates FHIR-based outcome reporting for chronic care. With 15 payers and 165M members aligned, here's what it means for developers.",
  "publishedAt": "2026-02-19T16:13:39.967Z",
  "tags": [
    "fhir",
    "cms",
    "interoperability",
    "compliance"
  ],
  "voice": "legal",
  "author": "FHIRfly",
  "seoTitle": "The CMS ACCESS Model: What FHIR Developers Need to Know | FHIRfly Blog",
  "seoDescription": "CMS's ACCESS model mandates FHIR-based outcome reporting for chronic care. With 15 payers and 165M members aligned, here's what it means for developers."
};

![CMS ACCESS Model and FHIR](/blog/heroes/cms-access-chronic-care-hero.png)

On July 5, 2026, CMS launches the ACCESS model — Advancing Chronic Care with Effective, Scalable Solutions. It is the most significant new payment model for technology-enabled chronic care in years, and it comes with a clear technical mandate: participants must report clinical outcomes through FHIR-based APIs.

If you're building healthcare software, this is worth understanding. ACCESS creates a new category of FHIR-dependent organizations that didn't exist before.

*This content is for informational purposes only and does not constitute legal advice. Consult with qualified legal counsel for compliance guidance specific to your situation.*

## What ACCESS Actually Is

ACCESS is a 10-year voluntary [CMMI](https://www.cms.gov/priorities/innovation/innovation-models/access) payment model that replaces traditional fee-for-service billing with fixed, outcome-aligned payments (OAPs) for managing chronic conditions in Original Medicare. Participating organizations — primarily technology vendors paired with Medicare-enrolled medical directors — receive annual per-beneficiary payments tied to measurable clinical improvement.

The model targets four clinical tracks:

| Track | Conditions | Key Outcome Measures | Annual Rate (Initial) |
|-------|-----------|---------------------|-----------------------|
| Early Cardio-Kidney-Metabolic (eCKM) | Hypertension, metabolic markers | Blood pressure, lipids, weight, HbA1c | $360 |
| Cardio-Kidney-Metabolic (CKM) | Diabetes, CKD, ASCVD | BP, lipids, HbA1c, eGFR, UACR | $420 |
| Musculoskeletal (MSK) | Chronic pain (3+ months) | Patient-reported outcomes (PROMs) | $180 |
| Behavioral Health (BH) | Depression, anxiety | PHQ-9, GAD-7, WHODAS 2.0 | $180 |

Payments are split: 50% disbursed quarterly upon G-code submission, 50% withheld for 12-month reconciliation based on clinical outcome attainment and avoided downstream utilization.

## The FHIR Mandate

This is the part that matters for developers. ACCESS participants must:

1. **Submit outcome measures via CMS's FHIR-based APIs** — blood pressure readings, HbA1c values, PHQ-9 scores, and patient-reported outcomes must flow to CMS through FHIR interfaces, not legacy flat files or manual reporting.

2. **Electronically share clinical updates with the beneficiary's other providers** — care coordination is a core requirement. When an ACCESS organization manages a patient's hypertension, they must share relevant clinical data with that patient's PCP and specialists.

3. **Maintain a FHIR API server** — CMS references the Health Tech Ecosystem pledge, which presumes FHIR capability for consent capture, eligibility checks, claims/clinical data integration, and bidirectional information sharing.

This is a meaningful shift. Previous CMS models accepted claims-based reporting or custom CSV uploads. ACCESS assumes API-driven data exchange as the baseline.

## Why the Multi-Payer Signal Matters

Fifteen major health plans representing 165 million covered lives have pledged to align with the ACCESS model's payment principles. The list includes Blue Shield of California, CVS Health, Humana, and UnitedHealthcare.

By publishing standardized G-codes and FHIR-based reporting specifications, CMS is explicitly inviting multi-payer adoption across Medicare Advantage, Medicaid, and commercial plans. This means the FHIR infrastructure built for ACCESS won't serve a single payer — it becomes the foundation for chronic care reporting across a participant's entire payer mix.

For developers, this amplifies the return on investment for building FHIR-native systems. The same FHIR API server, the same terminology mappings, and the same outcome reporting pipeline work across all aligned payers.

## What Developers Need to Build

ACCESS creates concrete technical requirements at several layers:

### Terminology and Clinical Coding

Every outcome measure maps back to standard clinical codes:

- **ICD-10** codes to identify qualifying conditions (hypertension, diabetes, CKD, depression)
- **LOINC** codes for laboratory results (HbA1c, eGFR, UACR, lipid panels) and assessment instruments (PHQ-9, GAD-7)
- **RxNorm** codes for medication management reporting
- **NPI** identifiers for provider attribution and care coordination
- **CPT/G-codes** for billing triggers

These aren't optional. FHIR resources require coded data — a blood pressure observation without a LOINC code is not a valid FHIR Observation.

### FHIR Resource Generation

ACCESS outcome reporting likely involves generating and submitting FHIR resources including:

- `Observation` resources for vitals and lab results
- `QuestionnaireResponse` resources for PROMs (PHQ-9, GAD-7, WHODAS 2.0)
- `Condition` resources for active problem lists
- `MedicationRequest` resources for treatment plans
- `CareTeam` and `CarePlan` resources for coordination

### Care Coordination Infrastructure

The bidirectional data sharing requirement means ACCESS organizations need to both send and receive clinical data from other providers. This maps directly to existing interoperability standards:

- **US Core FHIR profiles** for structured clinical data
- **SMART on FHIR** for authorized data access
- **TEFCA** or direct FHIR exchange for cross-organizational data flow

## The AI Angle

The payment rates are intentionally modest ($180–$420 per patient per year). Industry observers note these rates favor organizations that can deliver care efficiently through technology — including AI-driven clinical decision support, remote monitoring automation, and predictive analytics.

CMS has indicated it will monitor outcomes through [FDA's TEMPO program](https://www.fda.gov/medical-devices/digital-health-center-excellence/total-evaluation-mitigation-and-product-review-outcomes-tempo-program) and may adjust rates upward over time based on demonstrated results. This creates a clear first-mover advantage for organizations that can achieve clinical outcomes at these rates from day one.

## Timeline

| Date | Milestone |
|------|-----------|
| January 12, 2026 | Applications opened |
| April 1, 2026 | Application deadline for first performance period |
| **July 5, 2026** | **Model launch** |
| June 30, 2036 | Model end (10-year duration) |

## Key Takeaways

- ACCESS mandates FHIR-based API reporting for clinical outcomes — this is not optional for participants.
- 15 major payers covering 165 million members have aligned with the model's principles, signaling multi-payer FHIR adoption beyond just Original Medicare.
- Developers building for ACCESS participants need accurate, current clinical terminology (ICD-10, LOINC, RxNorm, NPI) to generate valid FHIR resources.
- The modest payment rates intentionally favor technology-driven care models, creating a market for AI-first healthcare organizations that will need FHIR-native tooling.
- The July 2026 launch date means organizations applying now are actively building their technical infrastructure.

## Further Reading

- [CMS ACCESS Model Overview](https://www.cms.gov/priorities/innovation/innovation-models/access) — Official CMMI page
- [ACCESS Payer Alignment Pledge](https://www.cms.gov/priorities/innovation/innovation-models/access) — List of aligned health plans
- [FDA TEMPO Program](https://www.fda.gov/medical-devices/digital-health-center-excellence/total-evaluation-mitigation-and-product-review-outcomes-tempo-program) — FDA's AI/ML monitoring framework
- [FHIRfly Clinical Coding APIs](https://fhirfly.io) — ICD-10, LOINC, RxNorm, and NPI lookup APIs for building FHIR-compliant systems
